import { Link } from 'react-router-dom'

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-bold text-text-primary mb-3">{title}</h2>
    <div className="text-sm text-text-secondary leading-relaxed space-y-3">{children}</div>
  </div>
)

export const PrivacyPolicy = () => (
  <div className="min-h-screen bg-background">
    <nav className="fixed top-0 inset-x-0 z-40 bg-background/90 backdrop-blur border-b border-border">
      <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="font-bold text-primary text-lg tracking-tight">DoorChart</Link>
        <Link to="/login" className="text-sm text-text-secondary hover:text-text-primary">Sign in</Link>
      </div>
    </nav>

    <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">
      <h1 className="text-3xl font-extrabold text-text-primary mb-2">Privacy Policy</h1>
      <p className="text-sm text-text-muted mb-10">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

      <Section title="Overview">
        <p>DoorChart ("we", "us", "our") is committed to protecting the privacy of your household's data. This policy explains what information we collect, how we use it, and your rights.</p>
      </Section>

      <Section title="Information We Collect">
        <p><strong>Account data:</strong> Your email address and display name when you sign up.</p>
        <p><strong>Household data:</strong> Events, tasks, shopping lists, meal plans, budget entries, health logs, and notes that you create within the app. This data is stored in your household's private database space.</p>
        <p><strong>Vault data:</strong> Items stored in the Vault are encrypted client-side using AES-256-GCM before being stored. We cannot read vault contents.</p>
        <p><strong>Payment data:</strong> Billing is handled by Stripe. We store only your subscription plan status — no card numbers are stored on our servers.</p>
        <p><strong>Usage data:</strong> Basic telemetry (page visits, errors) to improve reliability. No personal content is included.</p>
      </Section>

      <Section title="How We Use Your Data">
        <p>We use your data solely to provide the DoorChart service. We do not sell, rent, or share your personal or household data with third parties for advertising purposes.</p>
        <p>We may share data with service providers (Supabase for database/auth, Stripe for payments, Vercel for hosting) under strict data processing agreements.</p>
      </Section>

      <Section title="Data Retention">
        <p>Your data is retained as long as your account is active. You can delete your account at any time from Settings → Profile, which permanently removes all associated household data within 30 days.</p>
      </Section>

      <Section title="Security">
        <p>All data is transmitted over HTTPS. Database access is governed by row-level security policies ensuring each household can only access its own data. Vault entries use client-side AES-256-GCM encryption.</p>
      </Section>

      <Section title="Your Rights">
        <p>Depending on your location, you may have the right to access, correct, or delete your personal data. To exercise these rights, contact us at <a href="mailto:privacy@doorchart.com" className="text-primary underline">privacy@doorchart.com</a>.</p>
      </Section>

      <Section title="Cookies">
        <p>DoorChart uses a session cookie for authentication only. No third-party tracking cookies are used.</p>
      </Section>

      <Section title="Changes">
        <p>We may update this policy periodically. We will notify you of material changes via email or in-app notification.</p>
      </Section>

      <Section title="Contact">
        <p>Questions? Email us at <a href="mailto:privacy@doorchart.com" className="text-primary underline">privacy@doorchart.com</a>.</p>
      </Section>
    </div>

    <footer className="py-8 px-4 border-t border-border">
      <div className="max-w-3xl mx-auto flex items-center gap-4 text-xs text-text-muted">
        <Link to="/"        className="hover:text-text-primary">Home</Link>
        <Link to="/terms"   className="hover:text-text-primary">Terms of Service</Link>
        <Link to="/help"    className="hover:text-text-primary">Help Center</Link>
        <a href="mailto:hello@doorchart.com" className="hover:text-text-primary">Contact</a>
      </div>
    </footer>
  </div>
)
